import { Box, ClickAwayListener, Icon, SearchField, SearchFieldProps, styled, StyledSearchFieldBox, Switch, Typography } from "@tm/components"
import { getStyleTheme } from "@tm/context-distribution"
import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { style as tStyle } from "typestyle"
import { useLocalization } from "@tm/localization"
import { validateField } from "@tm/utils"
import { SearchFilters } from "@tm/models"
import { GetSearchFiltersRequest, SearchFilter } from "@bundles/parts/data/model"
import { Repositories } from "@bundles/parts/data"
import Hint from "./components/Hint"
import QueryHistory from "./components/QueryHistory"
import { SearchFiltersComponent } from "./components/SearchFiltersComponent"

function getStyle() {
    const theme = getStyleTheme()

    return {
        searchoptions: tStyle({
            position: "absolute",
            background: theme.colors.light,
            zIndex: 5,
            minWidth: "500px",
        }),
        gap: tStyle({
            padding: theme.margin.m,
        }),
        outsideClickWrapper: tStyle({ position: "relative" }),
    }
}

const SearchFieldBox = styled(StyledSearchFieldBox)<{ width?: number; maxWidth?: string }>(({ width, maxWidth }) => ({
    width,
    maxWidth,
}))

type Props = SearchFieldProps & {
    minimumSearchLength: number
    tooltip?: string
    searchFieldBoxMaxWidth?: string
    onSearchFiltersChanged(searchFilters: SearchFilters): void
    onStartSearchWithQuery(query: string, selectedSearchFilter: SearchFilters): void
}

export function DirectSearchField(props: Props) {
    const localization = useLocalization()
    const { translateText } = localization
    const [hint, setHint] = useState<string | undefined>(undefined)
    const queryParams = new URLSearchParams(window.location.search)
    const [extendedSearch, setExtendedSearch] = useState(queryParams.get("query")?.includes("*") ?? false)
    const startSearchFilters = queryParams.get("searchFilter")
    const [searchFilters, setSearchFilters] = useState(startSearchFilters === null ? SearchFilters.All : parseInt(startSearchFilters))
    const [showPopup, setShowPopup] = useState(false)
    const [searchFilterResultCounts, setSearchFilterResultCounts] = useState<SearchFilter[]>([])
    const searchFieldRef = useRef<HTMLDivElement>(null)
    const style = getStyle()
    const {
        onSearchFiltersChanged,
        onChange,
        onStartSearch,
        onStartSearchWithQuery,
        minimumSearchLength,
        value,
        tooltip,
        searchFieldBoxMaxWidth,
        ...rest
    } = props

    const handleSearch = useCallback(
        (newSearchQuery: string, isExtendedSearch: boolean, selectedSearchFilter: SearchFilters) => {
            let searchQuery = newSearchQuery
            if (searchQuery.length < minimumSearchLength) {
                setHint(translateText(812).replace("{0}", minimumSearchLength.toString()))
                return
            }

            let wildcardSearch = false
            let normalizedValue = searchQuery

            if (searchQuery.charAt(searchQuery.length - 1) === "*") {
                wildcardSearch = true
                searchQuery = searchQuery.slice(0, -1) // remove * from string, so it can be added again, after the length has been checked.
            } else if (isExtendedSearch) {
                wildcardSearch = true
            }

            normalizedValue = searchQuery.replace(/[^a-zA-Z0-9*]/g, "") // underscore "_" is not allowed, therefore not \w

            const state = validateField(normalizedValue).min(wildcardSearch ? 5 : minimumSearchLength, translateText(1231))

            if (state.messages.length) {
                setHint(state.messages[0])
                return
            }

            if (wildcardSearch) {
                searchQuery += "*"
            }

            onChange(searchQuery)
            setShowPopup(false)
            onStartSearchWithQuery(searchQuery, selectedSearchFilter)
        },
        [minimumSearchLength, translateText, onChange, onStartSearchWithQuery]
    )

    const handleExtendedSearchSwitch = useCallback(
        (checked: boolean) => {
            setExtendedSearch(checked)

            let selectedSearchFilter = searchFilters
            if (checked) {
                selectedSearchFilter = SearchFilters.All
            }
            setSearchFilters(selectedSearchFilter)

            const newQuery = value.replaceAll("*", "")

            onChange(newQuery)
            handleSearch(newQuery, checked, selectedSearchFilter)
        },
        [handleSearch, onChange, searchFilters, value]
    )

    const resultCountsRequest = useMemo(() => {
        const trimmedQuery = value.trim()
        if (!trimmedQuery || trimmedQuery.length < minimumSearchLength) {
            return
        }
        return {
            query: trimmedQuery,
            searchFilter: SearchFilters.All,
            extendedSearch,
        } as GetSearchFiltersRequest
    }, [value, minimumSearchLength, extendedSearch])

    useEffect(() => {
        if (resultCountsRequest) {
            Repositories.getSearchFilters(resultCountsRequest).then((result) => {
                setSearchFilterResultCounts(result)
            })
        }
    }, [resultCountsRequest])

    useEffect(() => {
        onSearchFiltersChanged(searchFilters)
    }, [onSearchFiltersChanged, searchFilters])

    return (
        <ClickAwayListener onClickAway={() => setShowPopup(false)}>
            <Box>
                <SearchField
                    size="large"
                    ref={searchFieldRef}
                    onFocus={() => setShowPopup(true)}
                    value={value}
                    onStartSearch={() => {
                        handleSearch(value, extendedSearch, searchFilters)
                        setShowPopup(false)
                    }}
                    onChange={onChange}
                    {...rest}
                >
                    {showPopup && (
                        <SearchFieldBox width={searchFieldRef.current?.offsetWidth} maxWidth={searchFieldBoxMaxWidth}>
                            {hint && <Hint hint={hint} />}
                            <Box pt={1} pl={2} gap={style.gap}>
                                <Switch
                                    size="small"
                                    labelPlacement="start"
                                    checked={extendedSearch}
                                    onChange={(_, checked) => handleExtendedSearchSwitch(checked)}
                                    label={translateText(12466)}
                                />
                            </Box>
                            <Box display="flex">
                                <Box display="flex" flexDirection="column" justifyContent="space-between">
                                    <div className={style.gap}>
                                        <QueryHistory
                                            handleSearch={(newQuery: string) => {
                                                handleSearch(newQuery, extendedSearch, searchFilters)
                                            }}
                                        />
                                    </div>
                                </Box>
                                <Box>
                                    <Box className={`search-filters-wrapper ${style.gap}`}>
                                        <SearchFiltersComponent
                                            isExtendedSearch={extendedSearch ?? false}
                                            showApplyButton={false}
                                            selectedSearchFilters={searchFilters}
                                            searchfilterResultCounts={searchFilterResultCounts}
                                            onSelection={(selectedFilter: SearchFilters) => {
                                                const newSelectedFilter = selectedFilter !== SearchFilters.All ? selectedFilter : SearchFilters.All
                                                setSearchFilters(newSelectedFilter)
                                                if (startSearchFilters) {
                                                    handleSearch(value, extendedSearch, newSelectedFilter)
                                                }
                                            }}
                                            localization={localization}
                                            toggleExtendedSearch={(extendedSearchValue: boolean) => {
                                                setExtendedSearch(extendedSearchValue)
                                            }}
                                        />
                                    </Box>
                                </Box>
                            </Box>
                            <Box display="flex" whiteSpace="initial" paddingLeft="11px" paddingBottom="0.75em">
                                <Box pr={0.625}>
                                    <Icon name="info" size="small" />
                                </Box>
                                <Typography variant="label">{translateText(12893)}</Typography>
                            </Box>
                        </SearchFieldBox>
                    )}
                </SearchField>
            </Box>
        </ClickAwayListener>
    )
}
